/**
 * useCockpitData — salud por dominio para la órbita del Centro de Mando de ARIA.
 *
 * Compone en cliente (sin endpoint nuevo) la "radiografía" que colorea cada
 * nodo-agente del orbe (AriaOrbitalBackground): parte de `GET /api/aria/inbox`
 * (mismo endpoint + queryKey que AriaInbox → React Query deduplica la petición si
 * ambos están montados), agrupa las sugerencias por `category` → id de agente del
 * orbe, y por agente calcula { status, count, headline }.
 *
 * - status: 'critical' (rojo) si su peor severidad es critical/high · 'warn'
 *   (ámbar) si es medium/low · 'ok' si no tiene pendientes.
 * - count: nº de pendientes del dominio (badge tipo notificación en el nodo).
 * - headline: titular real del item más urgente { title, detail, actionUrl } →
 *   se muestra al hover y el badge hace deep-link a `actionUrl`.
 *
 * `alertLevel` (0..1) agrega la presión global de trabajo → alimenta la
 * respiración/tensión del núcleo vivo (LivingCore) de forma honesta (peso
 * por severidad), en vez del recuento crudo de señales.
 *
 * Fuente de verdad de dominios: ariaDomainTokens (AGENT_VISUAL_DOMAIN). Severidad
 * unificada vía severityHelpers (mismo vocabulario que el resto de la app).
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/api/apiFetch';
import { normalizeSeverity, compareSeverity } from '@/lib/severityHelpers';

// Categoría del inbox → id de agente del orbe (AGENTS en AriaOrbitalBackground).
// Los ids `banking`/`hr` reutilizan las paletas treasury/payroll (ver
// AGENT_VISUAL_DOMAIN). `reconciliation` = conciliación bancaria → banking.
const CATEGORY_TO_AGENT = {
  fiscal: 'fiscal',
  sii: 'fiscal',
  // Ejes de dominio (InboxDomain) — el backend siempre manda `domain`; estas
  // claves permiten agrupar por él (ver abajo) sin perder las categorías legacy.
  accounting: 'accounting',
  banking: 'banking',
  posting: 'accounting',
  reconciliation: 'banking',
  treasury: 'banking',
  advances: 'accounting',
  crm: 'crm',
  invoicing: 'invoicing',
  expenses: 'expenses',
  payroll: 'hr',
  hr: 'hr',
  inventory: 'inventory',
  projects: 'projects',
  performance: 'performance',
  automation: 'automation',
  grants: 'grants',
  analytics: 'analytics',
};

// Peso por severidad para el nivel de alerta global (0..1).
const SEVERITY_WEIGHT = { critical: 1, high: 0.7, medium: 0.35, low: 0.15 };

// status del nodo a partir de la peor severidad de su dominio.
function statusFromSeverity(sev) {
  if (sev === 'critical' || sev === 'high') return 'critical';
  if (sev === 'medium' || sev === 'low') return 'warn';
  return 'ok';
}

/**
 * Fuente ÚNICA del inbox de ARIA en el cliente (`GET /api/aria/inbox`), que en
 * backend ya agrega las 7 fuentes —incluidos los avisos proactivos, como
 * `source:'proactive'`— y las ordena por severidad. Su docstring se declara "the
 * single source of truth for the ARIA Signal Center"; esto es su reflejo en FE.
 *
 * La queryKey se comparte a propósito con AriaInbox y con la campana
 * (`NotificationCenter`): los tres piden lo mismo, así que una sola petición los
 * sirve y un dismiss invalida a los tres a la vez.
 *
 * @param {string|undefined} companyId
 * @returns {{ data: {suggestions:Array, total:number, dismissed_count:number}|undefined, isLoading: boolean }}
 */
export function useAriaInboxSuggestions(companyId) {
  return useQuery({
    // eslint-disable-next-line local/no-literal-querykey -- key compartida con AriaInbox y NotificationCenter (aria/inbox global)
    queryKey: ['aria-inbox', companyId, 'all'],
    queryFn: async () => {
      const res = await apiFetch('/api/aria/inbox');
      return res ?? { suggestions: [], total: 0, dismissed_count: 0 };
    },
    enabled: !!companyId,
    staleTime: 60 * 1000,
  });
}

/**
 * @param {string|undefined} companyId
 * @returns {{ agentsHealth: Record<string, {status:string,count:number,severity:string,headline:{title:string,detail:string,actionUrl:string|null}}>, alertLevel: number, isLoading: boolean }}
 */
export function useCockpitData(companyId) {
  const { data, isLoading } = useAriaInboxSuggestions(companyId);

  return useMemo(() => {
    // Aún sin cargar → agentsHealth null (NO {}), para no afirmar "todo al día"
    // ni pintar calma antes de tener datos reales.
    if (!data) return { agentsHealth: null, alertLevel: 0, awaitingOk: 0, isLoading };

    const suggestions = Array.isArray(data.suggestions) ? data.suggestions : [];
    const byAgent = {};
    let scoreSum = 0;
    let awaitingOk = 0;

    for (const s of suggestions) {
      // `domain` es el eje REAL del aviso (siempre presente); `category` es el
      // campo legacy del briefing y los proactivos lo hardcodean a 'crm' — que
      // era el bug: TODOS los avisos proactivos (facturas vencidas incluidas)
      // coloreaban el nodo CRM del orbe. Domain primero, category de fallback.
      const agentId = CATEGORY_TO_AGENT[s.domain] ?? CATEGORY_TO_AGENT[s.category];
      if (!agentId) continue;
      const sev = normalizeSeverity(s.severity);
      scoreSum += SEVERITY_WEIGHT[sev] ?? SEVERITY_WEIGHT.low;
      // Proposal (kind:'proposal') = acción autónoma que ARIA espera aprobar.
      const isProposal = s.kind === 'proposal';
      if (isProposal) awaitingOk += 1;

      const cur = byAgent[agentId] ?? { count: 0, awaitingOk: 0, severity: 'low', headline: null };
      cur.count += 1;
      if (isProposal) cur.awaitingOk += 1;
      // La peor severidad manda; el headline se toma del item más urgente.
      if (cur.headline == null || compareSeverity(sev, cur.severity) < 0) {
        cur.severity = sev;
        cur.headline = {
          title: s.title ?? '',
          detail: s.description ?? '',
          actionUrl: s.action_url ?? null,
        };
      }
      byAgent[agentId] = cur;
    }

    const agentsHealth = {};
    for (const [agentId, v] of Object.entries(byAgent)) {
      agentsHealth[agentId] = {
        status: statusFromSeverity(v.severity),
        count: v.count,
        awaitingOk: v.awaitingOk,
        severity: v.severity,
        headline: v.headline,
      };
    }

    // Presión global: suma ponderada saturada. ~6 puntos de score = "lleno".
    const alertLevel = Math.min(1, scoreSum / 6);

    return { agentsHealth, alertLevel, awaitingOk, isLoading };
  }, [data, isLoading]);
}

export default useCockpitData;
